import React, { useCallback, useEffect, useMemo, useState } from "react";
import Chartist from "react-chartist";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  ProgressBar,
  Row,
  Spinner
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faBell,
  faCheckCircle,
  faCog,
  faDesktop,
  faExclamationTriangle,
  faGift,
  faMoneyBillWave,
  faShoppingCart,
  faSignOutAlt,
  faStar,
  faSyncAlt,
  faTicketAlt,
  faUndo,
  faUserPlus,
  faUsers,
  faWallet
} from "@fortawesome/free-solid-svg-icons";
import { useCookies } from "react-cookie";
import { Link, useHistory } from "react-router-dom";
import { Routes } from "../../routes";
import { logout } from "../../controller/api/auth";
import { getDashboardSummary } from "../../controller/api/statistic/dashboard";
import SystemNotificationBanner from "../../components/SystemNotificationBanner";
import PendingOrdersModal from "./PendingOrdersModal";

const pad = value => String(value).padStart(2, "0");
const dateKey = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const shiftDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const QUICK_RANGES = [
  { days: 1, label: "1 ngày" },
  { days: 7, label: "7 ngày" },
  { days: 30, label: "30 ngày" },
  { days: 90, label: "90 ngày" }
];

const money = value => new Intl.NumberFormat("vi-VN", {
  style: "currency",
  currency: "VND",
  maximumFractionDigits: 0
}).format(Number(value || 0));

const number = value => new Intl.NumberFormat("vi-VN").format(Number(value || 0));

const changePercent = (current, previous) => {
  const currentValue = Number(current || 0);
  const previousValue = Number(previous || 0);
  if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue)) return null;
  if (!previousValue) return currentValue ? null : 0;
  return ((currentValue - previousValue) / previousValue) * 100;
};

const MetricCard = ({ title, value, rawValue, icon, color, comparison, subtitle }) => {
  const percent = comparison === undefined ? undefined : changePercent(rawValue, comparison);
  const rising = percent !== undefined && percent !== null && percent >= 0;

  return (
    <Card className="metric-card border-0 shadow-sm h-100">
      <Card.Body className="p-4">
        <div className="d-flex justify-content-between align-items-start">
          <div className="min-w-0">
            <div className="text-muted small fw-bold text-uppercase mb-2">{title}</div>
            <div className="h3 fw-bolder mb-1 text-dark">{value}</div>
            {subtitle && <div className="small text-muted">{subtitle}</div>}
          </div>
          <span className="metric-icon" style={{ color, backgroundColor: `${color}18` }}>
            <FontAwesomeIcon icon={icon} />
          </span>
        </div>
        {percent !== undefined && (
          <div className={`small fw-bold mt-3 ${rising ? "text-success" : "text-danger"}`}>
            {percent === null ? (
              <><FontAwesomeIcon icon={faArrowUp} className="me-1" />Có phát sinh mới</>
            ) : (
              <>
                <FontAwesomeIcon icon={rising ? faArrowUp : faArrowDown} className="me-1" />
                {Math.abs(percent).toFixed(1)}% <span className="text-muted fw-normal">so với kỳ trước</span>
              </>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const DashboardCard = ({ title, icon, bgAccent, link, onClick }) => (
  <Nav.Link href={link} onClick={onClick} className="p-0 h-100 text-decoration-none">
    <Card className="shortcut-card border-0 shadow-sm h-100">
      <Card.Body className="d-flex align-items-center p-3">
        <span className="shortcut-icon me-3" style={{ backgroundColor: `${bgAccent}18`, color: bgAccent }}>
          <FontAwesomeIcon icon={icon} />
        </span>
        <span className="fw-bold text-dark">{title}</span>
      </Card.Body>
    </Card>
  </Nav.Link>
);

export default () => {
  const [cookies, , removeCookie] = useCookies(["lottmekong"]);
  const history = useHistory();
  const permission = (localStorage.getItem("permission") || "").split("");
  const today = useMemo(() => new Date(), []);
  const [range, setRange] = useState({ from: dateKey(today), to: dateKey(today) });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [showPendingOrders, setShowPendingOrders] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getDashboardSummary(cookies.lottmekong, range);
      if (!response) throw new Error("Dữ liệu trả về không hợp lệ.");
      setData(response);
      setUpdatedAt(new Date(response.generatedAt || Date.now()));
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Không thể tải dữ liệu thống kê.");
    } finally {
      setLoading(false);
    }
  }, [cookies.lottmekong, range]);

  useEffect(() => {
    document.title = "Bảng điều khiển";
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const choosePreset = days => setRange({
    from: dateKey(shiftDays(today, -(days - 1))),
    to: dateKey(today)
  });

  const isPresetActive = days => (
    range.from === dateKey(shiftDays(today, -(days - 1))) &&
    range.to === dateKey(today)
  );

  const handleDate = event => {
    const { name, value } = event.target;
    setRange(current => ({ ...current, [name]: value }));
  };

  const handleLogout = async event => {
    if (event) event.preventDefault();
    try {
      await logout(cookies.lottmekong);
    } catch (_error) {
      // The local session must still be cleared when the server is unavailable.
    } finally {
      removeCookie("lottmekong");
      history.replace("/");
    }
  };

  const overview = data?.overview || {};
  const previous = data?.previous || {};
  const operations = data?.operations || { devices: {} };
  const trend = data?.trend || [];
  const trendChart = {
    labels: trend.map(item => item.day.slice(5).split("-").reverse().join("/")),
    series: [
      trend.map(item => Number(item.sales || 0)),
      trend.map(item => Number(item.netRevenue || 0))
    ]
  };
  const chartOptions = {
    showArea: true,
    fullWidth: true,
    chartPadding: { top: 14, right: 18, bottom: 0, left: 8 },
    axisX: { showGrid: false, labelInterpolationFnc: (label, index) => {
      const step = trend.length > 14 ? 5 : trend.length > 7 ? 2 : 1;
      return index % step === 0 || index === trend.length - 1 ? label : null;
    } },
    axisY: { offset: 88, labelInterpolationFnc: value => number(value) }
  };

  const maxProductRevenue = Math.max(...(data?.products || []).map(item => Number(item.revenue)), 1);
  return (
    <main className="dashboard-page">
      <style>{`
        .dashboard-page { min-height: 100vh; background: #f3f4f6; padding-bottom: 3rem; }
        .dashboard-page .page-title { font-weight: 800; letter-spacing: -.035em; color: #111827; }
        .metric-card, .shortcut-card { border-radius: 14px; transition: transform .2s ease, box-shadow .2s ease; }
        .metric-card:hover, .shortcut-card:hover { transform: translateY(-3px); box-shadow: 0 12px 24px rgba(15,23,42,.09) !important; }
        .metric-icon, .shortcut-icon { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; border-radius: 12px; }
        .metric-icon { width: 46px; height: 46px; font-size: 1.15rem; }
        .shortcut-icon { width: 42px; height: 42px; }
        .dashboard-panel { border: 0; border-radius: 14px; box-shadow: 0 2px 10px rgba(15,23,42,.05); }
        .dashboard-trend-chart { height: 310px; }
        .dashboard-trend-chart .ct-series-a .ct-line { stroke: #3b82f6; stroke-width: 3px; }
        .dashboard-trend-chart .ct-series-a .ct-area { fill: #3b82f6; fill-opacity: .12; }
        .dashboard-trend-chart .ct-series-a .ct-point { stroke: #2563eb; stroke-width: 7px; }
        .dashboard-trend-chart .ct-series-b .ct-line { stroke: #10b981; stroke-width: 3px; }
        .dashboard-trend-chart .ct-series-b .ct-area { fill: #10b981; fill-opacity: .07; }
        .dashboard-trend-chart .ct-series-b .ct-point { stroke: #059669; stroke-width: 7px; }
        .dashboard-trend-chart .ct-grid { stroke: #e5e7eb; stroke-dasharray: 3px; }
        .dashboard-trend-chart .ct-label { color: #6b7280; fill: #6b7280; font-size: 11px; }
        .filter-bar { background: #fff; border-radius: 14px; box-shadow: 0 2px 10px rgba(15,23,42,.05); }
        .product-row + .product-row { border-top: 1px solid #eef0f3; }
        .operation-number { font-size: 1.65rem; font-weight: 800; color: #111827; }
        .operation-link { display: inline-block; padding: 0; border: 0; background: none; text-decoration: none; }
        .operation-link:hover .operation-number { color: #2563eb; text-decoration: underline; }
        .revenue-hero { background: linear-gradient(135deg, #064e3b, #059669); color: #fff; border-radius: 12px; }
        .revenue-detail { background: #f8fafc; border: 1px solid #eef0f3; border-radius: 12px; }
        .chart-legend-dot { width: 9px; height: 9px; display: inline-block; border-radius: 50%; }
        .min-w-0 { min-width: 0; }
        @media (max-width: 767.98px) { .dashboard-trend-chart { height: 250px; } }
      `}</style>

      <SystemNotificationBanner />
      <Container className="pt-5">
        <div className="d-lg-flex justify-content-between align-items-end mb-4">
          <div>
            <h1 className="page-title h2 mb-1">Bảng Điều Khiển</h1>
            <p className="text-muted mb-0">Tổng quan hoạt động kinh doanh và vận hành hệ thống.</p>
          </div>
          {updatedAt && (
            <div className="small text-muted mt-2 mt-lg-0">
              Cập nhật lúc {updatedAt.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>

        <div className="filter-bar p-3 mb-4">
          <div className="d-flex flex-wrap align-items-end gap-2">
            <div className="d-flex gap-2 me-lg-2">
              {QUICK_RANGES.map(item => (
                <Button
                  key={item.days}
                  size="sm"
                  variant={isPresetActive(item.days) ? "primary" : "outline-primary"}
                  onClick={() => choosePreset(item.days)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Từ ngày</Form.Label>
              <Form.Control size="sm" type="date" name="from" value={range.from} max={range.to} onChange={handleDate} />
            </Form.Group>
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Đến ngày</Form.Label>
              <Form.Control size="sm" type="date" name="to" value={range.to} min={range.from} max={dateKey(today)} onChange={handleDate} />
            </Form.Group>
            <Button size="sm" variant="light" className="ms-lg-auto" onClick={loadDashboard} disabled={loading}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading} className="me-2" />Làm mới
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" className="d-flex justify-content-between align-items-center">
            <span><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />{error}</span>
            <Button size="sm" variant="danger" onClick={loadDashboard}>Thử lại</Button>
          </Alert>
        )}

        {loading && !data ? (
          <div className="text-center py-6"><Spinner animation="border" variant="primary" /><div className="text-muted mt-3">Đang tổng hợp số liệu...</div></div>
        ) : data && (
          <>
            <Row className="g-3 mb-4">
              <Col xs={12} sm={6} xl={3}><MetricCard title="Doanh số" value={money(overview.sales)} rawValue={overview.sales} icon={faMoneyBillWave} color="#2563eb" comparison={previous.sales} /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Đơn hàng" value={number(overview.orders)} rawValue={overview.orders} icon={faShoppingCart} color="#8b5cf6" comparison={previous.orders} /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Số vé" value={number(overview.tickets)} rawValue={overview.tickets} icon={faTicketAlt} color="#f59e0b" subtitle="Tất cả sản phẩm" /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Khách hàng mới" value={number(overview.newUsers)} rawValue={overview.newUsers} icon={faUserPlus} color="#10b981" comparison={previous.newUsers} subtitle={`${number(overview.totalUsers)} khách hàng toàn hệ thống`} /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Tiền nạp" value={money(overview.charges)} rawValue={overview.charges} icon={faWallet} color="#06b6d4" comparison={previous.charges} /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Đã trả thưởng" value={money(overview.rewards)} rawValue={overview.rewards} icon={faGift} color="#ec4899" /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Đã rút" value={money(overview.withdrawals)} rawValue={overview.withdrawals} icon={faArrowDown} color="#ef4444" /></Col>
              <Col xs={12} sm={6} xl={3}><MetricCard title="Khách đang hoạt động" value={number(overview.activeUsers)} rawValue={overview.activeUsers} icon={faUsers} color="#6366f1" subtitle={`${overview.totalUsers ? ((overview.activeUsers / overview.totalUsers) * 100).toFixed(1) : 0}% tổng khách hàng`} /></Col>
            </Row>

            <Card className="dashboard-panel mb-4">
              <Card.Header className="border-0 bg-white pt-4 px-4 pb-2">
                <h5 className="mb-1">Thống kê doanh thu</h5>
                <div className="small text-muted">Doanh thu thuần = doanh số + phí dịch vụ − hoàn tiền thủ công − thưởng thực trả. Doanh số đã trừ vé huỷ.</div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <Row className="g-3 align-items-stretch">
                  <Col lg={4}>
                    <div className="revenue-hero h-100 p-4 d-flex flex-column justify-content-between">
                      <div>
                        <div className="small text-white-50 fw-bold text-uppercase mb-2">Doanh thu thuần ước tính</div>
                        <div className="display-6 fw-bolder mb-1">{money(overview.netRevenue)}</div>
                        <div className="small text-white-50">Biên doanh thu {Number(overview.revenueMargin || 0).toFixed(1)}%</div>
                      </div>
                      <div className="mt-4">
                        {changePercent(overview.netRevenue, previous.netRevenue) === null ? (
                          <span className="small fw-bold"><FontAwesomeIcon icon={faArrowUp} className="me-1" />Có phát sinh mới</span>
                        ) : (
                          <span className="small fw-bold">
                            <FontAwesomeIcon icon={changePercent(overview.netRevenue, previous.netRevenue) >= 0 ? faArrowUp : faArrowDown} className="me-1" />
                            {Math.abs(changePercent(overview.netRevenue, previous.netRevenue) || 0).toFixed(1)}% so với kỳ trước
                          </span>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col lg={8}>
                    <Row className="g-3 h-100">
                      <Col sm={6}>
                        <div className="revenue-detail h-100 p-3">
                          <div className="d-flex justify-content-between text-muted small fw-bold mb-2"><span>Doanh số bán vé</span><FontAwesomeIcon icon={faMoneyBillWave} className="text-primary" /></div>
                          <div className="h4 fw-bolder mb-0">{money(overview.sales)}</div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="revenue-detail h-100 p-3">
                          <div className="d-flex justify-content-between text-muted small fw-bold mb-2"><span>Phí dịch vụ thu được</span><FontAwesomeIcon icon={faWallet} className="text-info" /></div>
                          <div className="h4 fw-bolder text-info mb-0">+{money(overview.purchaseFees)}</div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="revenue-detail h-100 p-3">
                          <div className="d-flex justify-content-between text-muted small fw-bold mb-2"><span>Hoàn tiền thủ công</span><FontAwesomeIcon icon={faUndo} className="text-warning" /></div>
                          <div className="h4 fw-bolder text-warning mb-0">−{money(overview.cashback)}</div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="revenue-detail h-100 p-3">
                          <div className="d-flex justify-content-between text-muted small fw-bold mb-2"><span>Thưởng thực trả</span><FontAwesomeIcon icon={faGift} className="text-danger" /></div>
                          <div className="h4 fw-bolder text-danger mb-0">−{money(overview.rewards)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row className="g-4 mb-4">
              <Col lg={8}>
                <Card className="dashboard-panel h-100">
                  <Card.Header className="border-0 bg-white pt-4 px-4 pb-0">
                    <h5 className="mb-1">Xu hướng doanh thu</h5>
                    <div className="d-flex flex-wrap gap-3 small text-muted mt-2">
                      <span><i className="chart-legend-dot me-2" style={{ background: "#2563eb" }} />Doanh số</span>
                      <span><i className="chart-legend-dot me-2" style={{ background: "#059669" }} />Doanh thu thuần</span>
                    </div>
                  </Card.Header>
                  <Card.Body className="px-2 pb-2">
                    <Chartist data={trendChart} options={chartOptions} type="Line" className="dashboard-trend-chart" />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Card className="dashboard-panel h-100">
                  <Card.Header className="border-0 bg-white pt-4 px-4 pb-2"><h5 className="mb-0">Theo sản phẩm</h5></Card.Header>
                  <Card.Body className="pt-1 px-4">
                    {data.products.map(product => (
                      <div className="product-row py-3" key={product.key}>
                        <div className="d-flex justify-content-between mb-2">
                          <span className="fw-bold">{product.name}</span>
                          <span className="fw-bold">{money(product.revenue)}</span>
                        </div>
                        <ProgressBar now={(Number(product.revenue) / maxProductRevenue) * 100} style={{ height: 6 }} variant={product.key === "vietlott" ? "danger" : product.key === "xskt" ? "warning" : "purple"} />
                        <div className="d-flex justify-content-between text-muted small mt-2">
                          <span>{number(product.orders)} đơn</span><span>{number(product.tickets)} vé</span>
                        </div>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="dashboard-panel mb-4">
              <Card.Header className="border-0 bg-white pt-4 px-4 pb-2"><h5 className="mb-0">Tình trạng vận hành</h5></Card.Header>
              <Card.Body className="px-4 pb-4">
                <Row className="g-4">
                  <Col md={4}>
                    <div className="small text-muted fw-bold text-uppercase mb-1">Chờ xử lý</div>
                    <button type="button" className="operation-link" onClick={() => setShowPendingOrders(true)}>
                      <span className="operation-number">{number(operations.pendingOrders)}</span>
                    </button>
                    <div className="small text-muted">vé / đơn đang trong hàng đợi</div>
                  </Col>
                  <Col md={4}>
                    <div className="small text-muted fw-bold text-uppercase mb-1">Yêu cầu rút tiền</div>
                    <Link to={Routes.CustomerWithdrawRequestApp.path} className="operation-link">
                      <span className="operation-number">{number(operations.pendingWithdrawals)}</span>
                    </Link>
                    <div className="small text-muted">yêu cầu đang chờ duyệt</div>
                  </Col>
                  <Col md={4}>
                    <div className="small text-muted fw-bold text-uppercase mb-1">Thiết bị trực tuyến</div>
                    <div className="operation-number text-success"><FontAwesomeIcon icon={faCheckCircle} className="me-2 fs-6" />{number(operations.devices.online)}/{number(operations.devices.total)}</div>
                    <ProgressBar className="mt-2" now={operations.devices.total ? (operations.devices.online / operations.devices.total) * 100 : 0} variant="success" style={{ height: 6 }} />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <h5 className="mb-3">Truy cập nhanh</h5>
            <Row className="g-3">
              {permission[0] === "1" && <Col xs={12} sm={6} lg={3}><DashboardCard title="Khách Hàng" icon={faUsers} bgAccent="#3b82f6" link={Routes.CustomerOverview.path} /></Col>}
              {permission[1] === "1" && <Col xs={12} sm={6} lg={3}><DashboardCard title="Cài Đặt" icon={faCog} bgAccent="#10b981" link={Routes.SettingOverview.path} /></Col>}
              {permission[1] === "1" && <Col xs={12} sm={6} lg={3}><DashboardCard title="Quản Lý Thiết Bị" icon={faDesktop} bgAccent="#6366f1" link={Routes.DeviceManagementOverview.path} /></Col>}
              {permission[2] === "1" && <Col xs={12} sm={6} lg={3}><DashboardCard title="Vietlott" icon={faTicketAlt} bgAccent="#ef4444" link={Routes.VietlottCart.path} /></Col>}
              {permission[3] === "1" && <Col xs={12} sm={6} lg={3}><DashboardCard title="XSKT" icon={faStar} bgAccent="#f59e0b" link={Routes.ClassicOverview.path} /></Col>}
              {permission[4] === "1" && <Col xs={12} sm={6} lg={3}><DashboardCard title="Loto" icon={faTicketAlt} bgAccent="#8b5cf6" link={Routes.LotoOverview.path} /></Col>}
              <Col xs={12} sm={6} lg={3}><DashboardCard title="Quản lý thông báo" icon={faBell} bgAccent="#0891b2" link={Routes.NotificationManagement.path} /></Col>
              <Col xs={12} sm={6} lg={3}><DashboardCard title="Đăng Xuất" icon={faSignOutAlt} bgAccent="#6b7280" onClick={handleLogout} /></Col>
            </Row>
          </>
        )}
      </Container>

      <PendingOrdersModal show={showPendingOrders} onHide={() => setShowPendingOrders(false)} />
    </main>
  );
};
